import merge from 'lodash/merge';
import { AppInfo } from '../typings/appInfo.js';
import { appendTrailingSlash } from '../utils.js';
import {
  CAppConfig,
  LaunchMode,
  MicroAppManager,
  MicroFrontendApp,
} from './app.js';
import { LATEST_VERSION } from '../constant.js';
import { SecurityContext } from '../typings/common.js';

const CONFIG_KEY = 'microFrontendApps';
const isCIBuild = (): boolean => process.env.CI === 'true';

const isProduction = (mode: LaunchMode) => {
  if (isCIBuild()) return true;
  return String(mode).toLowerCase() === LaunchMode.PRODUCTION.toLowerCase();
};

const getPathName = (url?: string) => (url ? new URL(url).pathname : '');

const getAbsoluteUrl = (url: string): string => {
  const a = document.createElement('a');
  a.href = url;
  return a.href;
};

const getVersionedPath = (path: string, version: string) => {
  const majorMinorVersion = version.match(/^(?:\d+\.\d+)*/g);
  return path.replace(
    /{SYSTEM_VERSION}/,
    (majorMinorVersion && majorMinorVersion[0]) || LATEST_VERSION,
  );
};

const isFileListValid = (files: string[]) => {
  if (!files || !files.length) return false;
  return files.every((file) => typeof file === 'string');
};

const validate = (id: string, mfeConfig: MicroFrontendApp) => {
  const { mode = LaunchMode.PRODUCTION } = mfeConfig;
  if (
    String(mode).toLowerCase() !== LaunchMode.PRODUCTION.toLowerCase() &&
    String(mode).toLowerCase() !== LaunchMode.DEVELOPMENT.toLowerCase()
  )
    throw new Error(`invalid mode '${String(mode)}' for ${id}`);
  const {
    production,
    development,
    microappManager = MicroAppManager.APPSDK,
  } = mfeConfig;
  if (
    String(microappManager).toLowerCase() ===
    MicroAppManager.APPSDK.toLowerCase()
  ) {
    if (String(mode).toLowerCase() === LaunchMode.PRODUCTION.toLowerCase()) {
      if (!production) {
        throw new Error(
          `microFrontendApps.${id} is missing production section in app.config.json`,
        );
      }
      if (!isFileListValid(production.files)) {
        throw new Error(`invalid value for production.files`);
      }
    }
    if (String(mode).toLowerCase() === LaunchMode.DEVELOPMENT.toLowerCase()) {
      if (!development) {
        throw new Error(
          `microFrontendApps.${id} is missing development section in app.config.json`,
        );
      }
      if (!isFileListValid(development.files)) {
        throw new Error(`invalid value for development.files`);
      }
    }
  }
};

const getConfig = ({
  id,
  config,
  version = 'latest',
}: {
  id: string;
  config: MicroFrontendApp;
  version?: string;
}): AppInfo => {
  const {
    mode = LaunchMode.PRODUCTION,
    production,
    development,
    ...restConfig
  } = config;
  const envConfig = isProduction(mode) ? production : development;

  const newConfig = merge(
    { id },
    {
      name: id,
      mode: LaunchMode.PRODUCTION,
      manifestPath: './{SYSTEM_VERSION}/',
      securityContext: SecurityContext.USER,
      isJsModule: true,
    },
    restConfig,
    { mode },
    envConfig,
  ) as AppInfo;

  newConfig.hostUrl = appendTrailingSlash(
    getAbsoluteUrl(newConfig.hostUrl || ''),
  );
  newConfig.homeRoute =
    appendTrailingSlash(newConfig.homeRoute) || getPathName(newConfig.hostUrl);
  newConfig.manifestPath = appendTrailingSlash(
    getVersionedPath(newConfig.manifestPath, version),
  );
  return newConfig;
};

export class CMicroFEConfig {
  #microFrontendApps: Array<AppInfo> = [];

  init = ({
    version,
    appConfig,
  }: {
    version?: string;
    appConfig: CAppConfig;
  }) => {
    const mfeAppsFromConfig: Record<string, MicroFrontendApp> =
      appConfig.get(CONFIG_KEY);
    if (!mfeAppsFromConfig) return;
    this.#microFrontendApps = Object.keys(mfeAppsFromConfig).map(
      (appId: string) => {
        validate(appId, mfeAppsFromConfig[appId]);
        return getConfig({
          id: appId,
          config: mfeAppsFromConfig[appId],
          version,
        });
      },
    );
  };

  getConfigById = (id: string): AppInfo | undefined =>
    this.#microFrontendApps.find((app) => app.id === id);
}
